/*
 * upload.ts — guest photo upload form: a drag-and-drop dropzone with a
 * removable-thumbnail grid, the "type your sharing code in ASCII" guard, and a
 * one-shot confetti burst on the ThankYou page. The form still posts normally
 * (no AJAX), so everything here is pure enhancement and no-JS-safe.
 */

export function initUpload(): void {
  initCodeInput();
  initChapter();
  initDropzone();
  initSuccessConfetti();
}

/* ------------------------------------------------------------ album chapter
 * GALLERY_PLAN.md AL6: the optional "Which moment is this?" pair. The sub-group select is rendered with every
 * chapter's options in <optgroup data-group>; keep only the chosen chapter's (flat), reset the choice when the
 * chapter changes (D3), relabel it by the chapter's type and hide it when that chapter has none.
 */
function initChapter(): void {
  const group = document.querySelector<HTMLSelectElement>("[data-uc-group]");
  const sub = document.querySelector<HTMLSelectElement>("[data-uc-sub]");
  const wrap = document.querySelector<HTMLElement>("[data-uc-sub-wrap]");
  const label = document.querySelector<HTMLElement>("[data-uc-sub-label]");
  if (!group || !sub || !wrap) return;

  const fallback = label?.textContent ?? "";
  const all = Array.from(sub.querySelectorAll<HTMLOptGroupElement>("optgroup[data-group]")).flatMap((g) =>
    Array.from(g.querySelectorAll("option")).map((o) => ({ value: o.value, text: o.textContent ?? "", group: g.dataset.group!, selected: o.selected })),
  );
  let prev = all.find((o) => o.selected)?.value ?? "";

  const rebuild = (): void => {
    const g = group.value;
    sub.replaceChildren(sub.options[0] ?? new Option(""));
    const mine = all.filter((o) => g && o.group === g);
    mine.forEach((o) => sub.add(new Option(o.text, o.value)));
    sub.value = mine.some((o) => o.value === prev) ? prev : "";
    wrap.hidden = mine.length === 0;
    if (label) label.textContent = group.selectedOptions[0]?.dataset.subLabel || fallback;
  };

  group.addEventListener("change", () => {
    prev = "";
    rebuild();
  });
  rebuild();
}

/* --------------------------------------------------------------- sharing code
 * The code is printed in Latin letters + digits. On a phone the IME often
 * defaults to Chinese, so a guest can paste/compose Han characters by accident.
 * Nudge the keyboard toward Latin (markup) and strip anything that slips
 * through (here).
 */
function initCodeInput(): void {
  const el = document.querySelector<HTMLInputElement>("[data-ascii-code]");
  if (!el) return;

  const strip = (): void => {
    const cleaned = el.value.replace(/[^A-Za-z0-9]/g, "");
    if (cleaned !== el.value) el.value = cleaned;
  };

  el.addEventListener("input", strip);
  el.addEventListener("compositionend", strip);
}

/* ------------------------------------------------------------------- dropzone */
function initDropzone(): void {
  const form = document.querySelector<HTMLFormElement>("[data-upload-form]");
  if (!form) return;

  const drop = form.querySelector<HTMLElement>("[data-upload-drop]");
  const input = form.querySelector<HTMLInputElement>('input[type="file"][name="photos"]');
  const grid = form.querySelector<HTMLUListElement>("[data-upload-grid]");
  if (!drop || !input || !grid) return;

  // Removing one file means rewriting input.files, which is only possible via a
  // DataTransfer. Without it, leave the native multi-file picker untouched.
  let canSync = typeof DataTransfer !== "undefined";
  try {
    if (canSync) void new DataTransfer();
  } catch {
    canSync = false;
  }
  if (!canSync) return;

  const maxFiles = parseInt(input.dataset.maxFiles || "10", 10);
  const maxBytes = parseInt(input.dataset.maxMb || "25", 10) * 1024 * 1024;
  const noteMsg = input.dataset.msgNote || "";
  const badTypeMsg = input.dataset.msgBadType || "";
  const removeMsg = input.dataset.msgRemove || "Remove";
  const accept = (input.getAttribute("accept") || "")
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);

  drop.classList.add("upload-drop--live");

  let items: File[] = [];
  let rejectedType = false;

  const keyOf = (f: File): string => `${f.name}|${f.size}|${f.lastModified}`;

  const badType = (f: File): boolean => {
    if (!accept.length) return false;
    const name = f.name.toLowerCase();
    return !accept.some((ext) => name.endsWith(ext));
  };

  // A non-photo drop (or an "All files" pick that dodges the accept filter) used
  // to be added anyway, leaving an un-removable broken thumbnail that then also
  // failed server-side. Keep those files out of the list entirely.
  const notPhoto = (f: File): boolean =>
    badType(f) || (!!f.type && !f.type.startsWith("image/"));

  const sync = (): void => {
    const dt = new DataTransfer();
    items.forEach((f) => dt.items.add(f));
    input.files = dt.files;
  };

  const render = (): void => {
    grid.textContent = "";
    drop.classList.toggle("has-files", items.length > 0);
    renderBadTypeNote();

    items.forEach((file, i) => {
      const li = document.createElement("li");
      li.className = "upload-thumb";
      if (file.size > maxBytes || badType(file)) li.classList.add("is-bad");

      if (file.type.startsWith("image/")) {
        const url = URL.createObjectURL(file);
        const img = document.createElement("img");
        img.src = url;
        img.alt = file.name;
        img.addEventListener("load", () => URL.revokeObjectURL(url), { once: true });
        li.appendChild(img);
      }

      const remove = document.createElement("button");
      remove.type = "button";
      remove.className = "upload-thumb__remove";
      remove.setAttribute("aria-label", removeMsg);
      remove.textContent = "×";
      remove.addEventListener("click", () => {
        items.splice(i, 1);
        rejectedType = false;
        render();
      });
      li.appendChild(remove);

      grid.appendChild(li);
    });

    if (items.length > 0) {
      const addItem = document.createElement("li");
      addItem.className = "upload-thumb upload-thumb--add";
      const addBtn = document.createElement("button");
      addBtn.type = "button";
      addBtn.className = "upload-thumb__add";
      addBtn.setAttribute("aria-label", drop.dataset.msgAdd || "Add more photos");
      addBtn.textContent = "+";
      addBtn.addEventListener("click", () => input.click());
      addItem.appendChild(addBtn);
      grid.appendChild(addItem);
    }

    if (noteMsg && items.length > maxFiles) {
      const note = document.createElement("li");
      note.className = "upload-drop__note";
      note.textContent = noteMsg;
      grid.appendChild(note);
    }

    sync();
  };

  // The bad-type note lives on the dropzone itself, not in the thumbnail grid —
  // the grid is hidden while empty, and a lone bad drop adds nothing to it.
  let badTypeNote: HTMLParagraphElement | null = null;
  const renderBadTypeNote = (): void => {
    if (badTypeMsg && rejectedType) {
      if (!badTypeNote) {
        badTypeNote = document.createElement("p");
        badTypeNote.className = "upload-drop__note upload-drop__note--bad";
        badTypeNote.setAttribute("role", "status");
        drop.appendChild(badTypeNote);
      }
      badTypeNote.textContent = badTypeMsg;
    } else if (badTypeNote) {
      badTypeNote.remove();
      badTypeNote = null;
    }
  };

  const addFiles = (files: File[]): void => {
    rejectedType = false;
    files.forEach((f) => {
      if (notPhoto(f)) {
        rejectedType = true;
        return;
      }
      if (!items.some((e) => keyOf(e) === keyOf(f))) items.push(f);
    });
    render();
  };

  input.addEventListener("change", () => {
    // A programmatic sync() never fires change, so every change is a user pick.
    addFiles(Array.from(input.files || []));
  });

  const stop = (e: Event): void => {
    e.preventDefault();
    e.stopPropagation();
  };

  ["dragenter", "dragover"].forEach((ev) =>
    drop.addEventListener(ev, (e) => {
      stop(e);
      drop.classList.add("is-dragover");
    })
  );
  ["dragleave", "dragend"].forEach((ev) =>
    drop.addEventListener(ev, (e) => {
      stop(e);
      if (ev === "dragleave" && drop.contains((e as DragEvent).relatedTarget as Node)) return;
      drop.classList.remove("is-dragover");
    })
  );
  drop.addEventListener("drop", (e) => {
    stop(e);
    drop.classList.remove("is-dragover");
    const dropped = Array.from((e as DragEvent).dataTransfer?.files || []);
    if (dropped.length) addFiles(dropped);
  });
}

/* ----------------------------------------------------------- ThankYou confetti */
function initSuccessConfetti(): void {
  const host = document.querySelector<HTMLElement>("[data-upload-success]");
  if (!host) return;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  const canvas = document.createElement("canvas");
  canvas.className = "confetti-canvas";
  host.appendChild(canvas);
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const dpr = window.devicePixelRatio || 1;
  const resize = (): void => {
    canvas.width = host.clientWidth * dpr;
    canvas.height = host.clientHeight * dpr;
  };
  resize();

  const styles = getComputedStyle(document.documentElement);
  const colors = [
    styles.getPropertyValue("--wf-gold").trim() || "#C6A15B",
    styles.getPropertyValue("--wf-gold-bright").trim() || "#E2D0A6",
    styles.getPropertyValue("--wf-red").trim() || "#6A1C23",
    styles.getPropertyValue("--wf-cream").trim() || "#F2E6D0",
  ];

  type Bit = { x: number; y: number; vx: number; vy: number; rot: number; vr: number; c: string; s: number };
  const cx = canvas.width / 2;
  const bits: Bit[] = Array.from({ length: 90 }, () => ({
    x: cx,
    y: canvas.height * 0.28,
    vx: (Math.random() - 0.5) * 9 * dpr,
    vy: (Math.random() * -7 - 3) * dpr,
    rot: Math.random() * Math.PI,
    vr: (Math.random() - 0.5) * 0.3,
    c: colors[(Math.random() * colors.length) | 0],
    s: (Math.random() * 5 + 4) * dpr,
  }));

  const gravity = 0.22 * dpr;
  let frame = 0;
  const tick = (): void => {
    frame++;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    bits.forEach((b) => {
      b.vy += gravity;
      b.x += b.vx;
      b.y += b.vy;
      b.rot += b.vr;
      ctx.save();
      ctx.translate(b.x, b.y);
      ctx.rotate(b.rot);
      ctx.fillStyle = b.c;
      ctx.fillRect(-b.s / 2, -b.s / 2, b.s, b.s * 0.6);
      ctx.restore();
    });
    if (frame < 200) requestAnimationFrame(tick);
    else ctx.clearRect(0, 0, canvas.width, canvas.height);
  };
  requestAnimationFrame(tick);
}
